/**
 * What a visitor should know about a program before the sign-in box asks for
 * their email: which program, from whom, what it costs, how long it takes, what
 * they earn. Built from the Program doc the unauthenticated /guide (App.vue) and
 * the public-program flow (Setup.vue) already fetch; rendered by ContactPhase.
 *
 * Every fact is derived from data that exists on the doc. Nothing is invented:
 * a missing price, duration or credential simply produces no line.
 */
export type JourneyKind =
  | 'setup'
  | 'orientation'
  | 'read'
  | 'research'
  | 'watch'
  | 'write'
  | 'build'
  | 'present'
  | 'record'
  | 'activity';

/**
 * One activity as a step of the journey. Everything comes from the Program doc: the name,
 * the relative deadline (days after joining), whether it is team work, and the widgets in
 * it, which say what the participant actually does there (watch, read, write, build, record).
 */
export interface JourneyStep {
  name: string;
  /** Days after joining the activity is due (enrollment-anchored), null when unset */
  day: number | null;
  team: boolean;
  /** What you do, from the activity's widgets; the glyph and the one-line description follow */
  kind: JourneyKind;
  /** One plain line of what happens here, e.g. "Watch the training videos" */
  does: string;
}

export interface ProgramJoinContext {
  programId: string;
  programName: string;
  organizationName: string | null;
  programImage: string | null;
  /** "$99 once" | "Free to join" | null when the price is unknown */
  priceLabel: string | null;
  /** "About 6 hours" from the activities' expectedDuration minutes, null when none set */
  hoursLabel: string | null;
  /** Days from joining to the last activity's deadline (relative anchors only); null when unset */
  spanDays: number | null;
  /** "About 8 weeks" from spanDays, null under two weeks or when unset */
  spanLabel: string | null;
  /** "Internship title when you finish" when the program defines credentials */
  credentialLabel: string | null;
  /** The awarded credential's name (the one issued on signoff, else the first), or null */
  credentialName: string | null;
  activityCount: number;
  /** Activity names in program order, for the overview ("what you will do") */
  activityNames: string[];
  /** Plain-text program description (root programDesc), or null */
  description: string | null;
  /** Who it is for, from the Start activity's own settings: "Ages 13 to 24" and the residency list */
  ageLabel: string | null;
  residencies: string[];
  /** What the Start activity's own rewards setting says, e.g. "Unpaid work experience"; null when unset */
  rewardLabel: string | null;
  /** Every activity in order as a step a stranger can picture: what you do, by which day, with whom */
  journey: JourneyStep[];
}

/** Widget names present on an activity -> what the participant does there. First match wins. */
const KIND_RULES: Array<[RegExp, JourneyKind, string]> = [
  [/m-checkoutform/i, 'setup', 'Make your account and set up your place'],
  [/m-orientation/i, 'orientation', 'A short orientation to how the program works'],
  [/m-videoask/i, 'record', 'Record your sign-off interview on video'],
  [/m-presentation/i, 'present', 'Record your demo: video and slides'],
  [/m-project-implementation/i, 'build', 'Build the project and log your progress'],
  [/m-project-content/i, 'write', 'Write it up and submit it'],
  [/m-video-links-viewer/i, 'watch', 'Watch the training videos'],
  [
    /metagrabber|m-resource-links-viewer|researchlinks/i,
    'research',
    'Research with the resources given'
  ],
  [/m-timeline-tree|m-list-item|m-acknowledge-btn/i, 'read', 'Read the project brief']
];

export function journeyOf(adks: any[], organizationName: string | null): JourneyStep[] {
  const org = organizationName || 'the organizer';
  const dayOf = (adk: any): number | null => {
    const ts = adk?.activitySettings?.timeSettings ?? adk?.timeSettings;
    const raw = ts?.dueDate;
    if (typeof raw !== 'string' || !raw.startsWith('{')) return null;
    try {
      const rel = JSON.parse(raw) as { value?: unknown; unit?: unknown; anchor?: unknown };
      const anchor =
        typeof (rel.anchor ?? ts?.dueDateAnchor) === 'string'
          ? String(rel.anchor ?? ts?.dueDateAnchor)
          : '';
      if (anchor && anchor !== 'enrollment') return null;
      const v = Number(rel.value);
      if (!Number.isFinite(v) || v <= 0) return null;
      const unit = (typeof rel.unit === 'string' ? rel.unit : 'days').toLowerCase();
      let mult = 1;
      if (unit.startsWith('week')) mult = 7;
      else if (unit.startsWith('month')) mult = 30;
      return Math.round(v * mult);
    } catch {
      return null;
    }
  };
  return adks
    .filter(adk => String(adk?.name ?? '').trim())
    .map(adk => {
      const rawName = String(adk.name).trim();
      const name =
        rawName.toLowerCase() === 'signoff'
          ? 'Sign-off'
          : rawName.charAt(0).toUpperCase() + rawName.slice(1);
      const widgets = (Array.isArray(adk.fields) ? adk.fields : [])
        .map((f: any) => String(f?.name ?? f?.model ?? f?.type ?? ''))
        .join(' ');
      const rule = KIND_RULES.find(([re]) => re.test(widgets));
      const lower = rawName.toLowerCase();
      let does = rule ? rule[2] : `Complete the ${name} activity`;
      // The name says more than the widgets on the writing steps.
      if (rule && rule[1] === 'write' && lower === 'ideate')
        does = 'Come up with ideas for the project';
      if (rule && rule[1] === 'write' && lower === 'pitch') does = 'Pitch the idea you will build';
      if (rule && rule[1] === 'read') does = `Read the project brief from ${org}`;
      return {
        name,
        day: dayOf(adk),
        team: adk?.isTeamAdk === true,
        kind: rule ? rule[1] : 'activity',
        does
      };
    });
}

/** The name a participant earns: prefer the credential issued on signoff, else the first named one. */
export function awardedCredentialName(credentials: unknown): string | null {
  if (!Array.isArray(credentials)) return null;
  const names = (c: any): string[] =>
    (Array.isArray(c?.credentials) ? c.credentials : [])
      .map((n: unknown) => String(n ?? '').trim())
      .filter(Boolean);
  const onSignoff = credentials.find(
    (c: any) => Array.isArray(c?.activities) && c.activities.includes('signoff') && names(c).length
  );
  const pick = onSignoff || credentials.find((c: any) => names(c).length);
  return pick ? names(pick)[0] : null;
}

function plainText(raw: unknown): string | null {
  const text = String(raw ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text || null;
}

function toPrice(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(String(raw).replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : null;
}

export function formatHours(totalMinutes: number): string | null {
  if (!(totalMinutes > 0)) return null;
  if (totalMinutes < 60) return `About ${Math.round(totalMinutes)} minutes`;
  const hours = Math.round((totalMinutes / 60) * 2) / 2; // nearest half hour
  const shown = Number.isInteger(hours) ? String(hours) : hours.toFixed(1);
  return `About ${shown} hour${hours === 1 ? '' : 's'}`;
}

/**
 * Largest relative deadline (anchor 'enrollment', either timeSettings location), in days.
 * Mirrors Nexus program-card.ts spanDaysOf; the BPEI programs carry 1..54 days from join.
 */
export function spanDaysOf(adks: any[]): number | null {
  const unitDays = (unit: string): number => {
    if (unit.startsWith('week')) return 7;
    if (unit.startsWith('month')) return 30;
    return 1;
  };
  const daysOf = (ts: any): number => {
    const raw = ts?.dueDate;
    if (typeof raw !== 'string' || !raw.startsWith('{')) return 0;
    try {
      const rel = JSON.parse(raw) as { value?: unknown; unit?: unknown; anchor?: unknown };
      const anchorRaw = rel.anchor ?? ts?.dueDateAnchor;
      const anchor = typeof anchorRaw === 'string' ? anchorRaw : '';
      if (anchor && anchor !== 'enrollment') return 0;
      const v = Number(rel.value);
      if (!Number.isFinite(v) || v <= 0) return 0;
      const unit = (typeof rel.unit === 'string' ? rel.unit : 'days').toLowerCase();
      return v * unitDays(unit);
    } catch {
      return 0;
    }
  };
  const max = adks.reduce(
    (acc: number, a: any) =>
      Math.max(acc, daysOf(a?.activitySettings?.timeSettings), daysOf(a?.timeSettings)),
    0
  );
  return max > 0 ? Math.round(max) : null;
}

export function buildProgramJoinContext(program: any): ProgramJoinContext | null {
  const programName = (program?.programName || '').toString().trim();
  if (!program || !programName) return null;
  const adks: any[] = Array.isArray(program.adks) ? program.adks : [];

  const minutes = adks.reduce((sum, adk) => {
    const m =
      adk?.activitySettings?.timeSettings?.expectedDuration ??
      adk?.timeSettings?.expectedDuration ??
      0;
    return sum + (typeof m === 'number' && m > 0 ? m : 0);
  }, 0);

  const price = toPrice(program.programPrice);
  let priceLabel: string | null = null;
  if (price !== null) priceLabel = price > 0 ? `$${price} once` : 'Free to join';

  const hasCredential = Array.isArray(program.credentials) && program.credentials.length > 0;
  const credentialName = awardedCredentialName(program.credentials);
  // The strip keeps the short generic pill; the overview names the credential.
  const credentialLabel = hasCredential ? 'Internship title when you finish' : null;

  const start = adks.find(adk => String(adk?.name ?? '').toLowerCase() === 'start');
  const startFields: any[] = Array.isArray(start?.fields) ? start.fields : [];
  const ages = startFields.find(f => f?.model === 'ageRange')?.setup?.ageRange;
  const ageLabel =
    Array.isArray(ages) &&
    ages.length === 2 &&
    ages.every((n: unknown) => Number.isFinite(Number(n)))
      ? `Ages ${Number(ages[0])} to ${Number(ages[1])}`
      : null;
  // Prod stores the list either as an array or as one comma-joined string (Kaiser); the
  // Start form already accepts both, so must the landing, or the most disqualifying fact
  // about the program never renders (found 2026-09-13).
  const residencyRaw = startFields.find(f => f?.model === 'requiredResidency')?.setup
    ?.requiredResidency;
  let residencyList: unknown[] = [];
  if (Array.isArray(residencyRaw)) residencyList = residencyRaw;
  else if (typeof residencyRaw === 'string') {
    // the lookahead absorbs the space so `\s*` cannot backtrack past it and split ", CA"
    residencyList = residencyRaw.split(/\s*,\s*(?!\s*[A-Z]{2}\b)/);
  }
  const residencies = residencyList
    .map((r: unknown) =>
      String(r ?? '')
        .replace(/,\s*[A-Z]{2}$/, '')
        .trim()
    )
    .filter(Boolean);
  const rewardsRaw = startFields.find(f => f?.model === 'rewards')?.setup?.rewards;
  const reward = (Array.isArray(rewardsRaw) ? rewardsRaw : [])
    .map((r: unknown) => String(r ?? '').trim())
    .find(Boolean);
  const rewardLabel = reward
    ? reward.charAt(0).toUpperCase() + reward.slice(1).toLowerCase()
    : null;

  const spanDays = spanDaysOf(adks);
  return {
    spanDays,
    spanLabel: spanDays && spanDays >= 14 ? `About ${Math.round(spanDays / 7)} weeks` : null,
    programId: (program._id || program.id || '').toString(),
    programName,
    organizationName: (program.organizationName || '').toString().trim() || null,
    programImage:
      typeof program.programImage === 'string' && /^https:\/\//.test(program.programImage)
        ? program.programImage
        : null,
    priceLabel,
    hoursLabel: formatHours(minutes),
    credentialLabel,
    credentialName,
    activityCount: adks.length,
    activityNames: adks
      .map(adk => String(adk?.name ?? '').trim())
      .filter(Boolean)
      .map(name => name.charAt(0).toUpperCase() + name.slice(1)),
    description: plainText(program.programDesc),
    ageLabel,
    residencies,
    rewardLabel,
    journey: journeyOf(adks, (program.organizationName || '').toString().trim() || null)
  };
}

/** The short fact lines shown under the program name, in reading order. */
export function joinFacts(ctx: ProgramJoinContext | null): string[] {
  if (!ctx) return [];
  const facts: string[] = [];
  if (ctx.activityCount > 0) {
    facts.push(`${ctx.activityCount} ${ctx.activityCount === 1 ? 'activity' : 'activities'}`);
  }
  if (ctx.hoursLabel) facts.push(ctx.hoursLabel);
  if (ctx.priceLabel) facts.push(ctx.priceLabel);
  if (ctx.credentialLabel) facts.push(ctx.credentialLabel);
  return facts;
}

export function programPageTitle(programName: string | null | undefined): string {
  const name = (programName || '')
    .replace(/^[\p{Emoji_Presentation}\p{Extended_Pictographic}\s]+/u, '')
    .trim();
  return name ? `${name} | PilotCity` : 'PilotCity  |  Build projects to earn internships';
}
